import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Building2, CreditCard, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsModule({ authHeaders }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/crm/settings`, { headers: authHeaders });
        setSettings(res.data);
      } catch (err) {
        setSettings({});
      }
    };
    fetchSettings();
  }, [authHeaders]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API_URL}/api/crm/settings`, settings, { headers: authHeaders });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => setSettings({ ...settings, [field]: value });

  if (!settings) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-[#DC2626] border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="settings-module">
      <form onSubmit={handleSave} className="space-y-6 max-w-3xl">
        {/* Company info */}
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 size={20} className="text-[#DC2626]" />
            <h3 className="font-semibold text-lg">Informations de l'entreprise</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Nom de l'entreprise</label>
              <input value={settings.name || ''} onChange={e => update('name', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="settings-company-name" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Téléphone</label>
              <input value={settings.phone || ''} onChange={e => update('phone', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Email</label>
              <input type="email" value={settings.email || ''} onChange={e => update('email', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Adresse</label>
              <input value={settings.address || ''} onChange={e => update('address', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Code postal</label>
              <input value={settings.postal_code || ''} onChange={e => update('postal_code', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Ville</label>
              <input value={settings.city || ''} onChange={e => update('city', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">SIREN</label>
              <input value={settings.siren || ''} onChange={e => update('siren', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="XXX XXX XXX" data-testid="settings-siren" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">SIRET</label>
              <input value={settings.siret || ''} onChange={e => update('siret', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="XXX XXX XXX XXXXX" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">N° TVA Intracommunautaire</label>
              <input value={settings.tva_number || ''} onChange={e => update('tva_number', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="FRXX XXXXXXXXX" />
            </div>
          </div>
        </div>

        {/* Bank info */}
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard size={20} className="text-[#DC2626]" />
            <h3 className="font-semibold text-lg">Coordonnées bancaires</h3>
          </div>
          <p className="text-xs text-gray-400 mb-4">Ces informations apparaissent sur vos devis et factures.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Banque</label>
              <input value={settings.bank_name || ''} onChange={e => update('bank_name', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="settings-bank-name" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">IBAN</label>
              <input value={settings.bank_iban || ''} onChange={e => update('bank_iban', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="FRXX XXXX XXXX XXXX XXXX XXXX XXX" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">BIC</label>
              <input value={settings.bank_bic || ''} onChange={e => update('bank_bic', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
        </div>

        {/* Document settings */}
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={20} className="text-[#DC2626]" />
            <h3 className="font-semibold text-lg">Paramètres des documents</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Préfixe devis</label>
              <input value={settings.quote_prefix || ''} onChange={e => update('quote_prefix', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="settings-quote-prefix" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Préfixe factures</label>
              <input value={settings.invoice_prefix || ''} onChange={e => update('invoice_prefix', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">TVA par défaut (%)</label>
              <input type="number" step="0.1" value={settings.default_tva || 20} onChange={e => update('default_tva', parseFloat(e.target.value) || 20)} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-xs text-gray-500 mb-1 block">Conditions générales (devis / factures)</label>
            <textarea value={settings.conditions || ''} onChange={e => update('conditions', e.target.value)} rows={3} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="settings-conditions" />
          </div>
        </div>

        {/* Save button */}
        <div className="flex items-center gap-4">
          <button type="submit" disabled={saving} className="flex items-center gap-2 bg-[#DC2626] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50" data-testid="save-settings-btn">
            <Save size={16} />
            {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
          </button>
          {saved && <span className="text-sm text-green-600 font-medium">Paramètres sauvegardés !</span>}
        </div>
      </form>
    </div>
  );
}
